import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Decimal } from 'decimal.js';
import debounce from 'lodash/debounce';
import { getPointsBalance } from 'store/user/userSelectors';
import usePointsConverters from 'hooks/points/usePointsConverters';
import { MIN_POINTS_AMOUNT } from 'config';
import { useDataLayer } from 'hooks/useDataLayer';
import { getCampaignOriginalTiers } from 'store/campaign/campaignSelectors';
import { useIsAuthenticated } from 'lib/oauth';
import { getLuxePointsLevels } from 'store/pointsBurnLuxe/pointsBurnLuxeSelectors';

export const DEFAULT_POINTS_AMOUNT = 20000;
const TRACKING_DEBOUCE_MILLIS = 300;

const emitInteraction = debounce((emitInteractionEvent) => {
  emitInteractionEvent({ type: 'Points and Pay Slider', value: `Slider Selected` });
}, TRACKING_DEBOUCE_MILLIS);

const getMaxCashAmount = ({ convertPointsToCash, totalCashAmount }) => {
  return new Decimal(totalCashAmount).minus(convertPointsToCash({ points: MIN_POINTS_AMOUNT }));
};

const getMinCashAmount = ({
  isAuthenticated,
  pointsBalanceInCash,
  totalCashAmount,
  payableAtPropertyCashAmount,
  payableAtBookingCashAmount,
}) => {
  if (!isAuthenticated) return payableAtPropertyCashAmount;

  if (pointsBalanceInCash.greaterThanOrEqualTo(payableAtBookingCashAmount)) {
    return payableAtPropertyCashAmount;
  } else {
    return totalCashAmount.minus(pointsBalanceInCash);
  }
};

const getInitialPointsAmount = ({ convertCashToPoints, totalCashAmount, minCashAmount, disableDefaultPointsLimit = false }) => {
  const maxPoints = convertCashToPoints({ cash: totalCashAmount.minus(minCashAmount) });
  return disableDefaultPointsLimit ? maxPoints : Decimal.min(maxPoints, new Decimal(DEFAULT_POINTS_AMOUNT));
};

const getInitialCashAmount = ({ convertPointsToCash, totalCashAmount, initialPointsAmount }) => {
  const initialPointsAmountInCash = convertPointsToCash({ points: initialPointsAmount });
  return Decimal.max(totalCashAmount.minus(initialPointsAmountInCash), new Decimal(0));
};

const usePointsPaySlider = ({ totalCash, payableAtProperty, initialPointsAmount, disableDefaultPointsLimit, isLuxeOffer }) => {
  if (totalCash.currency === 'PTS') throw new Error('Total must be supplied to points pay slider in cash');

  const isAuthenticated = useIsAuthenticated();
  const pointsBalance = useSelector(getPointsBalance);
  const originalTier = useSelector(getCampaignOriginalTiers);
  const luxeTiers = useSelector(getLuxePointsLevels);

  const useCurrentTiers = usePointsConverters();
  const useLuxeTiers = usePointsConverters(luxeTiers);

  const { convertCashToPoints, convertPointsToCash } = isLuxeOffer ? useLuxeTiers : useCurrentTiers;
  const { convertCashToPoints: preVppConvertCashToPoints } = usePointsConverters(originalTier);

  const { emitInteractionEvent } = useDataLayer();

  const pointsBalanceInCash = pointsBalance ? convertPointsToCash({ points: pointsBalance }) : new Decimal(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const totalCashAmount = new Decimal(totalCash.amount);
  const payableAtPropertyCashAmount = payableAtProperty ? new Decimal(payableAtProperty.amount) : new Decimal(0);
  const payableAtBookingCashAmount = totalCashAmount.minus(payableAtPropertyCashAmount);
  const maxCashAmount = getMaxCashAmount({ convertPointsToCash, totalCashAmount });

  const minCashAmount = getMinCashAmount({
    convertPointsToCash,
    convertCashToPoints,
    isAuthenticated,
    pointsBalanceInCash,
    totalCashAmount,
    payableAtPropertyCashAmount,
    payableAtBookingCashAmount,
    maxCashAmount,
  });

  initialPointsAmount = initialPointsAmount
    ? new Decimal(initialPointsAmount)
    : getInitialPointsAmount({
        convertPointsToCash,
        convertCashToPoints,
        totalCashAmount,
        minCashAmount,
        disableDefaultPointsLimit,
      });

  const initialCashAmount = getInitialCashAmount({ convertPointsToCash, totalCashAmount, initialPointsAmount });

  const [pointsAmount, setPointsAmount] = useState(initialPointsAmount);
  const [cashAmount, setCashAmount] = useState(initialCashAmount);

  const pointsAmountInCash = totalCashAmount.minus(cashAmount);
  const originalPointsAmount = preVppConvertCashToPoints({ cash: pointsAmountInCash });

  const onChange = useCallback(
    (value) => {
      setPointsAmount(convertCashToPoints({ cash: totalCashAmount.minus(value) }));
      setCashAmount(new Decimal(value));
      emitInteraction(emitInteractionEvent);
    },
    [convertCashToPoints, emitInteractionEvent, totalCashAmount],
  );

  const onChangeSet = useCallback(
    (value) => {
      const pointsAmount = convertCashToPoints({ cash: totalCashAmount.minus(value) });
      const pointsToSet = pointsAmount.toNumber() > pointsBalance.toNumber() ? pointsBalance : pointsAmount;
      setPointsAmount(pointsToSet);
      setCashAmount(new Decimal(value));
      emitInteraction(emitInteractionEvent);
    },
    [convertCashToPoints, emitInteractionEvent, totalCashAmount, pointsBalance],
  );

  return {
    hasDiscount: !originalPointsAmount.equals(pointsAmount),
    originalPointsAmount,
    pointsAmount,
    pointsAmountInCash,
    cashAmount,
    minCashAmount,
    maxCashAmount,
    initialCashAmount,
    onChange,
    onChangeSet,
  };
};

export default usePointsPaySlider;
